import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import config from "../config";
import { useNavigate } from "react-router-dom";
import "./MyToolsPage.css"; // We will create this CSS file next
import edit_grey from "../public/img/edit_grey.svg";
// Helper to check if content is SVG (can be reused or imported if you have a central utils)
const isSvgContent = (str) => {
  if (!str) return false;
  return typeof str === "string" && str.trim().startsWith("<");
};

// Generate a consistent pastel color based on the tag string (from DirectoryPage)
const getTagColor = (tag) => {
  const hash = tag.split("").reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc);
  }, 0);
  const h = Math.abs(hash) % 360;
  
  // Check if dark mode is active
  const isDarkMode = document.documentElement.getAttribute('data-theme') === 'dark';
  
  if (isDarkMode) {
    // Dark mode: darker background with brighter text
    return `hsl(${h}, 45%, 30%)`; // Darker, more saturated background
  } else {
    // Light mode: pastel colors
    return `hsl(${h}, 70%, 80%)`;
  }
};

const MyToolsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [myTools, setMyTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user || !user.first_name || !user.last_name) {
      // Ensure first_name and last_name are present
      setError("User details not available. Cannot fetch tools.");
      setLoading(false);
      return;
    }

    const fetchMyTools = async () => {
      setLoading(true);
      setError(null);
      try {
        // Construct query parameters for firstName and lastName
        const queryParams = new URLSearchParams({
          firstName: user.first_name,
          lastName: user.last_name,
        }).toString();

        const response = await fetch(
          `${config.api.baseUrl}/dashboard/by-creator?${queryParams}`,
          {
            method: "GET",
            credentials: "include",
          }
        );
        const data = await response.json();
        if (data.success) {
          setMyTools(data.chatbots || []);
        } else {
          setError(data.error || "Failed to fetch your tools.");
        }
      } catch (err) {
        setError("An error occurred while fetching your tools.");
        console.error("Error fetching my tools:", err);
      }
      setLoading(false);
    };

    fetchMyTools();
  }, [user]);

  const handleToolClick = (tool) => {
    // Assuming all tools navigate to the chat page
    navigate(`/chat?id=${tool._id}`);
  };

  // Map provider names to color classes (from DirectoryPage)
  const getProviderColorClass = (model) => {
    if (!model) return "";
    const modelLower = model.toLowerCase();
    if (modelLower.includes("gpt") || modelLower.includes("openai"))
      return "my-tools-tag-openai";
    if (modelLower.includes("gemini") || modelLower.includes("google"))
      return "my-tools-tag-google";
    if (modelLower.includes("claude") || modelLower.includes("anthropic"))
      return "my-tools-tag-anthropic";
    return "my-tools-tag-other";
  };

  return (
    <Layout>
      <div className="my-tools-page-wrapper">
        <div className="my-tools-page-container">
          <div className="my-tools-header">
          <h1>My Tools</h1>
          {user && (
            <p>
              Tools created by you ({user.first_name} {user.last_name})
            </p>
          )}
        </div>

        {loading && (
          <div className="my-tools-loading">Loading your tools...</div>
        )}
        {error && <div className="my-tools-error">{error}</div>}

        {!loading && !error && (
          <div className="my-tools-cards-container">
            {myTools.length > 0 ? (
              myTools.map((tool) => (
                <div
                  key={tool._id}
                  className="my-tools-card"
                  onClick={() => handleToolClick(tool)}
                >
                  <div className="my-tools-card-header">
                    <div className="my-tools-icon">
                      {tool.icon ? (
                        isSvgContent(tool.icon) ? (
                          <div
                            className="my-tools-svg-container"
                            dangerouslySetInnerHTML={{ __html: tool.icon }}
                          />
                        ) : (
                          <img src={tool.icon} alt={tool.title || "Tool"} />
                        )
                      ) : (
                        <div className="my-tools-default-icon">
                          {(tool.title || "?").charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="my-tools-card-header-main">
                      <div className="my-tools-tags">
                        {tool.tags &&
                          tool.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="my-tools-tag"
                              style={{ backgroundColor: getTagColor(tag) }}
                            >
                              {tag}
                            </span>
                          ))}
                        {tool.model && (
                          <span
                            className={`my-tools-tag ${getProviderColorClass(
                              tool.model
                            )}`}
                          >
                            {tool.model}
                          </span>
                        )}
                        {tool.provider && (
                          <span className="my-tools-tag my-tools-tag-provider">
                            {tool.provider}
                          </span>
                        )}
                      </div>
                      <img
                        src={edit_grey}
                        alt="Edit"
                        className="my-tools-edit-icon"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent card click
                          navigate(`/edit-tool/${tool._id}`);
                        }}
                      />
                    </div>
                  </div>
                  <div className="my-tools-content">
                    <h3>{tool.title || "Untitled Tool"}</h3>
                    {tool.description && <p>{tool.description}</p>}
                  </div>
                </div>
              ))
            ) : (
              <div className="my-tools-no-items-message">
                You haven't created any tools yet.
              </div>
            )}
          </div>
        )}
        </div>
      </div>
    </Layout>
  );
};

export default MyToolsPage;
